/** 工作流执行中等待用户在 UI 上确认（侵权风险等） */
const infringementWaiters = new Map();

function infrKey(taskId, rowIndex) {
  return `${taskId}:${rowIndex}`;
}

/**
 * @returns {Promise<'continue'|'skip'|'stopItem'|'terminate'>}
 */
export function waitForInfringementDecision(taskId, rowIndex) {
  return new Promise((resolve) => {
    infringementWaiters.set(infrKey(taskId, rowIndex), resolve);
  });
}

/** @param {'continue'|'skip'|'stopItem'|'terminate'} decision */
export function submitInfringementDecision(taskId, rowIndex, decision) {
  const key = infrKey(taskId, rowIndex);
  const resolve = infringementWaiters.get(key);
  if (!resolve) return false;
  infringementWaiters.delete(key);
  if (decision === 'continue') {
    resolve('continue');
  } else {
    resolve(decision === 'terminate' ? 'terminate' : decision === 'stopItem' ? 'stopItem' : 'skip');
  }
  return true;
}

export function cancelInfringementWaiters(taskId) {
  infringementWaiters.forEach((resolve, key) => {
    if (key.startsWith(`${taskId}:`)) {
      resolve('skip');
      infringementWaiters.delete(key);
    }
  });
}

/** 工作流「主图创建 / POD 商品设计」步骤：等待用户分配印花后再继续合成 */
const goodsStampWaiters = new Map();

function goodsStampKey(taskId, rowIndex) {
  return `${taskId}:${rowIndex}`;
}

/**
 * @returns {Promise<{ goodsForms: object[], stampRefs: object[] }>}
 */
export function waitForGoodsStampAssignment(taskId, rowIndex) {
  return new Promise((resolve, reject) => {
    goodsStampWaiters.set(goodsStampKey(taskId, rowIndex), { resolve, reject });
  });
}

/** @param {{ goodsForms: object[], stampRefs: object[] }} payload */
export function submitGoodsStampAssignment(taskId, rowIndex, payload) {
  const key = goodsStampKey(taskId, rowIndex);
  const waiter = goodsStampWaiters.get(key);
  if (!waiter) return false;
  goodsStampWaiters.delete(key);
  waiter.resolve(payload);
  return true;
}

export function cancelGoodsStampWaiters(taskId, reason = '任务已终止') {
  goodsStampWaiters.forEach((waiter, key) => {
    if (key.startsWith(`${taskId}:`)) {
      waiter.reject(new Error(reason));
      goodsStampWaiters.delete(key);
    }
  });
}

/** 预分配模式：各素材 upstream 产出登记与合并等待 */
const preAssignOutputStore = new Map();
const preAssignOutputWaiters = new Map();
const preAssignGoodsDoneStore = new Map();
const preAssignGoodsDoneWaiters = new Map();

function preAssignOutputKey(taskId) {
  return String(taskId);
}

function preAssignGoodsDoneKey(taskId, executorRowIndex) {
  return `${taskId}:${executorRowIndex}`;
}

export function registerPreAssignRowStampOutput(taskId, rowId, output) {
  const key = preAssignOutputKey(taskId);
  const store = { ...(preAssignOutputStore.get(key) || {}) };
  store[String(rowId)] = output;
  preAssignOutputStore.set(key, store);

  const waiters = preAssignOutputWaiters.get(key);
  if (waiters?.size) {
    [...waiters].forEach((fn) => {
      try { fn(); } catch { /* ignore */ }
    });
  }
  return store;
}

export function getPreAssignRowStampOutputs(taskId) {
  return { ...(preAssignOutputStore.get(preAssignOutputKey(taskId)) || {}) };
}

/** 预分配等待默认超时（与工作流商品步骤量级一致），避免 notify 不到时永久挂起 */
const PRE_ASSIGN_WAIT_TIMEOUT_MS = 600000;

export function waitForPreAssignRowStampOutputs(
  taskId,
  neededRowIds = [],
  { timeoutMs = PRE_ASSIGN_WAIT_TIMEOUT_MS } = {},
) {
  const needed = [...new Set(neededRowIds.map(String).filter(Boolean))];
  const tryResolve = () => {
    const latest = getPreAssignRowStampOutputs(taskId);
    if (needed.every((id) => latest[id]?.url && latest[id]?.spaceId)) {
      return latest;
    }
    return null;
  };

  const ready = tryResolve();
  if (ready) return Promise.resolve(ready);

  const key = preAssignOutputKey(taskId);
  return new Promise((resolve, reject) => {
    const set = preAssignOutputWaiters.get(key) || new Set();
    let settled = false;
    let wrapped = null;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      if (wrapped) set.delete(wrapped);
      reject(new Error('等待预分配印花产出超时'));
    }, Math.max(1000, Number(timeoutMs) || PRE_ASSIGN_WAIT_TIMEOUT_MS));
    wrapped = () => {
      const latest = tryResolve();
      if (!latest || settled) return;
      settled = true;
      window.clearTimeout(timer);
      set.delete(wrapped);
      resolve(latest);
    };
    wrapped.reject = (err) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      set.delete(wrapped);
      reject(err instanceof Error ? err : new Error(String(err || '预分配等待已取消')));
    };
    set.add(wrapped);
    preAssignOutputWaiters.set(key, set);
  });
}

export function notifyPreAssignGoodsComplete(taskId, executorRowIndex, result) {
  const key = preAssignGoodsDoneKey(taskId, executorRowIndex);
  preAssignGoodsDoneStore.set(key, result);
  const waiters = preAssignGoodsDoneWaiters.get(key);
  if (waiters?.size) {
    waiters.forEach((waiter) => {
      try {
        if (typeof waiter === 'function') waiter(result);
        else waiter?.resolve?.(result);
      } catch { /* ignore */ }
    });
    preAssignGoodsDoneWaiters.delete(key);
  }
}

export function waitForPreAssignGoodsComplete(
  taskId,
  executorRowIndex,
  { timeoutMs = PRE_ASSIGN_WAIT_TIMEOUT_MS } = {},
) {
  const key = preAssignGoodsDoneKey(taskId, executorRowIndex);
  const existing = preAssignGoodsDoneStore.get(key);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const set = preAssignGoodsDoneWaiters.get(key) || new Set();
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      set.delete(waiter);
      reject(new Error('等待预分配合并合成结果超时'));
    }, Math.max(1000, Number(timeoutMs) || PRE_ASSIGN_WAIT_TIMEOUT_MS));
    const waiter = {
      resolve: (result) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        set.delete(waiter);
        resolve(result);
      },
      reject: (err) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        set.delete(waiter);
        reject(err instanceof Error ? err : new Error(String(err || '预分配等待已取消')));
      },
    };
    set.add(waiter);
    preAssignGoodsDoneWaiters.set(key, set);
  });
}

export function clearPreAssignRuntimeState(taskId, reason = '预分配等待已取消') {
  const idKey = preAssignOutputKey(taskId);
  const outputWaiters = preAssignOutputWaiters.get(idKey);
  if (outputWaiters?.size) {
    const err = new Error(reason);
    [...outputWaiters].forEach((fn) => {
      try { fn.reject?.(err); } catch { /* ignore */ }
    });
  }
  preAssignOutputStore.delete(idKey);
  preAssignOutputWaiters.delete(idKey);
  [...preAssignGoodsDoneStore.keys()].forEach((key) => {
    if (key.startsWith(`${taskId}:`)) preAssignGoodsDoneStore.delete(key);
  });
  [...preAssignGoodsDoneWaiters.keys()].forEach((key) => {
    if (!key.startsWith(`${taskId}:`)) return;
    const waiters = preAssignGoodsDoneWaiters.get(key);
    if (waiters?.size) {
      const err = new Error(reason);
      [...waiters].forEach((waiter) => {
        try {
          if (typeof waiter?.reject === 'function') waiter.reject(err);
        } catch { /* ignore */ }
      });
    }
    preAssignGoodsDoneWaiters.delete(key);
  });
}

export function cancelPreAssignWaiters(taskId, reason = '任务已终止') {
  clearPreAssignRuntimeState(taskId, reason);
}

export function buildInitialRuntimeStepOverrides(template) {
  const ov = {};
  const modelStudioNavIds = ['modelTryOn', 'modelHandheld', 'modelUniversalWear'];
  (template?.steps || []).forEach((s) => {
    const navId = s?.navId;
    if (!navId) return;
    const p = s.params && typeof s.params === 'object' ? s.params : {};
    if (['goods', 'titles', 'platformListing', 'podProductDesign', ...modelStudioNavIds].includes(navId)) {
      ov[navId] = { ...p };
    }
  });
  return ov;
}

export function applyRuntimeStepOverridesToRows(rows, runtimeStepOverrides) {
  if (!runtimeStepOverrides || typeof runtimeStepOverrides !== 'object') {
    return rows;
  }
  return rows.map((row) => {
    const stepOverrides = { ...(row.stepOverrides || {}) };
    Object.entries(runtimeStepOverrides).forEach(([navId, params]) => {
      if (!params || typeof params !== 'object') return;
      stepOverrides[navId] = { ...(stepOverrides[navId] || {}), ...params };
    });
    return { ...row, stepOverrides };
  });
}

export function resolveListingTemplateIds(task, template) {
  const fromRuntime = task?.runtimeStepOverrides?.platformListing?.listingTemplateIds;
  if (Array.isArray(fromRuntime) && fromRuntime.length > 0) {
    return fromRuntime.map(String).filter(Boolean);
  }
  const step = (template?.steps || []).find((s) => s.navId === 'platformListing');
  const fromStep = step?.params?.listingTemplateIds;
  if (Array.isArray(fromStep) && fromStep.length > 0) {
    return fromStep.map(String).filter(Boolean);
  }
  const single = step?.params?.listingTemplateId;
  if (single) return [String(single)];
  return [];
}
